import type { QualityIssue } from '../contract/types.js';
import type { Detection, DetectorContext } from './detector.js';
import type { BinaryImage } from './threshold.js';

/**
 * The ink ledger: what a scan contained, and how much of it the detector named.
 *
 * The vector pipeline asserts `consumed + recorded === total` over PDF operators
 * (ADR 0004), so nothing a drawing contains can vanish uncounted. A raster image
 * has no operators. It has ink, and ink is what this counts:
 *
 *     explained + located + diffuse === total ink
 *
 * **explained** is ink inside the support band of a reported detection.
 * **located** is residual ink, grouped into components, each one carrying its own
 * bounding box. **diffuse** is residual ink in components too small to locate
 * usefully -- counted, never dressed up as a finding.
 *
 * This is accounting and not accuracy. No detection is suppressed here and no
 * gated figure moves; what changes is that the ink a scan contains and the
 * detector could not name is on the record instead of nowhere.
 */

/**
 * Half the width of the band a detection explains, **in points**.
 *
 * A stroke on paper has width and a detection is a zero-width geometric ideal,
 * so a band of zero width would explain almost none of the ink lying on a
 * correctly detected rule. Every value here is physical and reaches pixels only
 * through `pixelsPerPoint`: `TRACE_GAP_PIXELS = 4` was a distance on paper
 * written as a number of pixels, and it cost the A3 sheet every rule on it at
 * 600 DPI while reading perfectly at 300.
 *
 * **The value is measured, and the thing measured is not the explained fraction.**
 * A band is too narrow when a correctly detected rule sheds hairline slivers of
 * its own antialiased edge into the residual — noise the ledger manufactures
 * about ink it did in fact explain. The count of those slivers is the number of
 * located regions lying **beside a detection**, and it is what the sweep reads.
 *
 * Over all 24 images, detections held fixed, only the band varied, **at the
 * shipped `MIN_REGION_POINTS` of 1 pt**:
 *
 *     band (pt)     0.25    0.50    0.75    1.00    1.25    1.50    2.00    3.00
 *     beside        9881    3582     366      59      48      48      48      48
 *     isolated      5778    5778    5778    5778    5778    5778    5778    5778
 *     explained %  37.24   59.96   62.92   63.07   63.11   63.14   63.20   63.34
 *
 * `beside` settles at 48 from 1.25 pt onward, and **1.25 is the smallest band at
 * which it does**: at 1.00 pt eleven slivers are still being shed. That is the
 * whole basis for the value.
 *
 * **The sweep bounds this band from below and not from above, and an earlier
 * version of this docstring claimed otherwise.** It argued that `isolated` —
 * regions near no detection at all, which is the text and the arcs — fell at
 * 3.00 pt, and read that as the band reaching past the stroke to swallow content
 * nothing detected. `isolated` is constant at every width measured. The figures
 * that claimed otherwise were real when they were written and were invalidated
 * by the commit that changed what `abuts` means, from a comparison of bounding
 * boxes to one against the detection's own geometry; the rows were never
 * re-swept. Even at the old numbers the reading was wrong: the three regions
 * that moved were moving from `isolated` to `beside`, and the located total was
 * unchanged at 5712. Nothing was being swallowed.
 *
 * So the upper end is not measured, and `explained` cannot supply one either —
 * it rises monotonically at every width, including every wrong one, so a rule
 * that maximises it can always be satisfied by widening the band. A figure that
 * only ever improves cannot choose anything. The band is chosen from below.
 */
const STROKE_BAND_POINTS = 1.25;

/**
 * The smallest residual component that gets its own bounding box, **in points**.
 *
 * Below this a component is counted as diffuse. A bounding box per speck is not
 * information: a degraded sheet carries thousands of them, and `RF01.300.noisy`
 * binarises to 0.0229 ink against the clean sheet's 0.0174 precisely because of
 * them. Those are the **adaptive** figures, which is the path `preprocess` takes
 * and therefore the only ink this module ever counts; global Otsu on the same
 * two sheets gives 0.0210 and 0.0165, and quoting those here was quoting a
 * measurement of a path the ledger does not run.
 *
 * **The clean sheets are the control, and that is what makes this measurable.**
 * A clean fixture carries no speckle, so anything the floor sends to diffuse on
 * a clean sheet is a real mark losing its location — a dot on an `i`, a piece of
 * punctuation. The floor may absorb speckle on the degraded sheets and must
 * discard nothing on the clean ones.
 *
 * Swept at a band of 1.25 pt, over all 24 images:
 *
 *     floor (pt)          0.50    0.75    1.00    1.25    1.50    1.75    2.00
 *     clean: located      2935    2935    2935    2935    2899    2887    2875
 *     clean: diffuse         0       0       0       0      36      48      60
 *     noisy: located      3107    2892    2891    2891    2891    2871    2837
 *
 * Both bounds are sharp and the margins are symmetric at half a point.
 * **Below**, at 0.50 pt, the degraded sheets locate 216 more regions than they
 * settle at — speckle being handed its own bounding box and reported as a
 * finding. **Above**, at 1.50 pt, thirty-six marks on sheets that contain no
 * speckle at all stop being located. One point sits between them with 0.5 pt of
 * clearance each way.
 *
 * The two errors are not symmetric in cost even though the margins are. Locating
 * a speck reports one issue too many, which a reader can dismiss. Demoting a
 * real mark deletes the only record of where it was, which is the thing this
 * module exists to prevent — so where the sweep left a choice, it went narrow.
 */
const MIN_REGION_POINTS = 1;

/**
 * The band in pixels, floored at one.
 *
 * Below a pixel the band cannot cover a rasterised stroke at all -- the ink is
 * quantised to the pixel grid, so a band narrower than a pixel explains only
 * whatever happens to land exactly on the line and leaves the rest of the same
 * stroke as residual. The floor is a statement about the raster, not about paper.
 */
export function bandHalfWidthPixels(
  context: DetectorContext,
  strokeBandPoints = STROKE_BAND_POINTS,
): number {
  return Math.max(1, strokeBandPoints * context.pixelsPerPoint);
}

/** The location floor in pixels, on the same terms as the band. */
export function minRegionPixels(
  context: DetectorContext,
  minRegionPoints = MIN_REGION_POINTS,
): number {
  return Math.max(1, minRegionPoints * context.pixelsPerPoint);
}

/**
 * Both constants, overridable — **for the sweep and for nothing else.**
 *
 * Same reason `houghLines` exposes `maxRefitPasses`: a value chosen over the
 * corpus has to be swept over the corpus, and a sweep that re-implements the
 * thing it is measuring is measuring the re-implementation. Nothing in the
 * product passes this, and the defaults are the measured values.
 *
 * **A change to `abuts`, to `reach`, or to what `covers` accepts invalidates the
 * `beside` and `isolated` rows of the band table above, and both tables must be
 * re-swept.** That has already happened once, silently: the commit that stopped
 * `abuts` comparing bounding boxes left a published measurement behind that no
 * reader could reproduce.
 */
export interface LedgerOptions {
  strokeBandPoints?: number;
  minRegionPoints?: number;
}

/** The constants, so a report can state what it measured with. */
export const LEDGER_CONSTANTS = {
  strokeBandPoints: STROKE_BAND_POINTS,
  minRegionPoints: MIN_REGION_POINTS,
} as const;

/**
 * Distance from a point to a **segment**, not to the line through it.
 *
 * The clamp on `t` is what caps the ends. Without it every detection would
 * explain ink along its own infinite extension, so a short tick mark would
 * account for a rule on the far side of the sheet that happens to be collinear
 * with it -- and the ledger would report a drawing fully explained by a detector
 * that found almost none of it.
 */
function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/**
 * Whether a detection's band covers a pixel.
 *
 * A circle is an **annulus** and never a disc. A disc would explain every pixel a
 * circle encloses, which on a drawing is the text, the hatching and whatever else
 * sits inside it -- the ledger would report the sheet accounted for because
 * something round was drawn around it.
 */
function covers(detection: Detection, x: number, y: number, half: number): boolean {
  if (detection.kind === 'line') {
    return distanceToSegment(x, y, detection.x1, detection.y1, detection.x2, detection.y2) <= half;
  }

  if (detection.kind === 'circle') {
    // Squared, so the hot path carries no square root. A circle's band box is
    // the filled square it encloses and every ink pixel inside it -- the text,
    // the hatching -- reaches this test, so it runs on far more pixels than the
    // annulus contains. Four axis-aligned strips would not fix that: the
    // annulus at 45 degrees sits in none of them.
    const dx = x - detection.cx;
    const dy = y - detection.cy;
    const d2 = dx * dx + dy * dy;
    const inner = Math.max(0, detection.r - half);
    const outer = detection.r + half;
    return d2 >= inner * inner && d2 <= outer * outer;
  }

  const { vertices, closed } = detection;
  const last = closed ? vertices.length : vertices.length - 1;
  for (let i = 0; i < last; i += 1) {
    const a = vertices[i]!;
    const b = vertices[(i + 1) % vertices.length]!;
    if (distanceToSegment(x, y, a.x, a.y, b.x, b.y) <= half) return true;
  }
  return false;
}

/**
 * `covers` under a name that says it is reached directly only by its tests.
 *
 * The production path goes through the mask, which walks ink rather than
 * geometry. Exporting the predicate lets the geometry have tests of its own
 * instead of being asserted through two layers of counting.
 */
export const coversForTest = covers;

export interface InkRegion {
  /** [minX, minY, maxX, maxY] in the detector's pixel frame, inclusive. */
  readonly bbox: readonly [number, number, number, number];
  readonly pixels: number;
  /** Indices of detections close enough to this region to be worth naming. */
  readonly abuts: number[];
  /**
   * One pixel index inside this component, so a consumer can walk the component
   * itself rather than its bounding box.
   *
   * A bounding box is not the region: components interleave, and the box of one
   * routinely encloses ink belonging to its neighbours. Anything asking a
   * question about *this* region's pixels — how much of it lies on some other
   * geometry, say — has to re-walk from here, and a box would silently answer
   * about whatever else happened to be passing through.
   */
  readonly seed: number;
}

export interface RasterLedger {
  readonly totalInk: number;
  readonly explained: number;
  /**
   * The coverage mask this account was computed from: 1 where a detection's
   * band lies over ink.
   *
   * Carried so that a consumer needing the residual does not build a second one.
   * The acceptance runner did exactly that and paid for a whole extra pass over
   * the sheet plus its allocations, on every image.
   */
  readonly mask: Uint8Array;
  /** Residual components big enough to point at. Mutable so a test can break it. */
  located: InkRegion[];
  /** Residual components below the location floor: counted, never located. */
  readonly diffuse: { regions: number; pixels: number };
  readonly perEntity: number[];
  readonly bandPoints: number;
  readonly minRegionPoints: number;
}

/**
 * The boxes a detection's band can possibly touch, in pixels.
 *
 * A polyline returns one box **per edge** rather than one box for the whole
 * shape, and on this corpus that is the difference between scanning a strip
 * along each of the drawing frame's four edges and scanning the entire sheet
 * four times over: the frame's own bounding box is very nearly the page.
 */
function bandBoxes(
  detection: Detection,
  half: number,
): { x0: number; y0: number; x1: number; y1: number }[] {
  const box = (ax: number, ay: number, bx: number, by: number) => ({
    x0: Math.floor(Math.min(ax, bx) - half),
    y0: Math.floor(Math.min(ay, by) - half),
    x1: Math.ceil(Math.max(ax, bx) + half),
    y1: Math.ceil(Math.max(ay, by) + half),
  });

  if (detection.kind === 'line') {
    return [box(detection.x1, detection.y1, detection.x2, detection.y2)];
  }
  if (detection.kind === 'circle') {
    return [
      box(
        detection.cx - detection.r,
        detection.cy - detection.r,
        detection.cx + detection.r,
        detection.cy + detection.r,
      ),
    ];
  }

  const { vertices, closed } = detection;
  const boxes: { x0: number; y0: number; x1: number; y1: number }[] = [];
  const last = closed ? vertices.length : vertices.length - 1;
  for (let i = 0; i < last; i += 1) {
    const a = vertices[i]!;
    const b = vertices[(i + 1) % vertices.length]!;
    boxes.push(box(a.x, a.y, b.x, b.y));
  }
  return boxes;
}

export interface ExplainedMask {
  /** One byte per pixel: 1 means this pixel is ink **and** some band covers it. */
  readonly mask: Uint8Array;
  /** How many ink pixels the mask holds. The only figure that may be summed. */
  readonly explained: number;
  /**
   * Ink within each detection's own band, in detection order.
   *
   * **This is not a partition and must never be summed against the total.** Two
   * rules crossing at a corner share their crossing pixels, and each of them
   * counts those pixels; the mask counts them once. It is a diagnostic — how
   * much evidence each detection is standing on — and nothing else.
   */
  readonly perEntity: number[];
}

/**
 * Which ink each detection accounts for.
 *
 * Walks each detection's band boxes rather than the whole image, so the cost
 * scales with what was detected instead of with the sheet, and tests ink before
 * geometry because the ink is 2% of the pixels and the distance is the expensive
 * part.
 */
export function explainedMask(
  image: BinaryImage,
  detections: readonly Detection[],
  context: DetectorContext,
  options: LedgerOptions = {},
): ExplainedMask {
  const { width, height, ink } = image;
  const half = bandHalfWidthPixels(context, options.strokeBandPoints);
  const mask = new Uint8Array(width * height);
  const perEntity: number[] = detections.map(() => 0);
  let explained = 0;

  for (let d = 0; d < detections.length; d += 1) {
    const detection = detections[d]!;
    const boxes = bandBoxes(detection, half);
    for (let b = 0; b < boxes.length; b += 1) {
      const region = boxes[b]!;
      const y0 = Math.max(0, region.y0);
      const y1 = Math.min(height - 1, region.y1);
      const x0 = Math.max(0, region.x0);
      const x1 = Math.min(width - 1, region.x1);
      for (let y = y0; y <= y1; y += 1) {
        for (let x = x0; x <= x1; x += 1) {
          const at = y * width + x;
          if (ink[at] === 0) continue;
          if (!covers(detection, x, y, half)) continue;
          // Already counted for this detection if an earlier of its own boxes
          // contained this pixel -- and containment is the whole test, with no
          // geometry in it, because `covers` asks about the WHOLE polyline
          // rather than the edge whose box we happen to be walking. A pixel
          // inside box 0's rectangle but lying on edge 3 is covered from box 0,
          // so testing it against edge 0 would fail to suppress it at box 3 and
          // the rectangle would credit itself twice. Measured: 92 pixels
          // against 80 with no guard, 84 with a per-edge one, 80 with this.
          //
          // The alternative was an Int32Array the size of the sheet recording
          // which detection last claimed each pixel -- 262 MB on the largest
          // image in the corpus, allocated inside a browser tab that had just
          // spent the same again on the canvas it rasterised from.
          if (inEarlierBox(boxes, b, x, y)) continue;
          if (mask[at] === 0) {
            mask[at] = 1;
            explained += 1;
          }
          perEntity[d] = perEntity[d]! + 1;
        }
      }
    }
  }

  return { mask, explained, perEntity };
}

/**
 * Whether an earlier box of the same detection already visited this pixel.
 *
 * Lines and circles have one box and return immediately. Only a polyline has
 * more, and only near its corners do they overlap, so this loops over almost
 * nothing.
 */
function inEarlierBox(
  boxes: readonly { x0: number; y0: number; x1: number; y1: number }[],
  upTo: number,
  x: number,
  y: number,
): boolean {
  for (let i = 0; i < upTo; i += 1) {
    const box = boxes[i]!;
    if (x >= box.x0 && x <= box.x1 && y >= box.y0 && y <= box.y1) return true;
  }
  return false;
}

/**
 * The whole account: what was ink, what a detection explained, what is left.
 *
 * The residual is labelled into 8-connected components. Four-connectivity would
 * be wrong for the same reason it is always wrong on line art: a one-pixel
 * stroke at any angle other than horizontal or vertical steps diagonally, so
 * four-connectivity reports a diagonal rule as a row of isolated specks and
 * calls every one of them diffuse.
 *
 * The fill is **iterative over an explicit stack**, and the reason is a bound
 * this corpus does not reach rather than one it does. Measured, the largest
 * residual component over all 24 images is 11,406 px on RF02.600.noisy — well
 * inside any engine's call stack, so nothing here would crash today. What is not
 * bounded is the input: a component is everything the detector did not name that
 * happens to touch, and on a drawing whose text and hatching connect it is a
 * fraction of a 65.6 Mpx sheet rather than eleven thousand pixels. A recursive
 * fill would then crash on the biggest sheet in front of it, which is exactly
 * where the last resolution defect lived. The measured figure is stated because
 * the alternative was the phrase "millions of pixels", which was never measured
 * and is 400 times the truth on this corpus.
 */
export function reconcileInk(
  image: BinaryImage,
  detections: readonly Detection[],
  context: DetectorContext,
  options: LedgerOptions = {},
): RasterLedger {
  const { width, height, ink } = image;
  const bandPoints = options.strokeBandPoints ?? STROKE_BAND_POINTS;
  const regionPoints = options.minRegionPoints ?? MIN_REGION_POINTS;

  // Counted by its own pass over the ink array, not derived from anything else.
  // This is the figure the other two are checked against.
  let totalInk = 0;
  for (let i = 0; i < ink.length; i += 1) totalInk += ink[i]!;

  const { mask, explained, perEntity } = explainedMask(image, detections, context, options);
  const floor = minRegionPixels(context, regionPoints);

  const seen = new Uint8Array(width * height);
  const stack: number[] = [];
  const located: InkRegion[] = [];
  let diffuseRegions = 0;
  let diffusePixels = 0;

  /**
   * How near a detection has to be for a region to be reported against it.
   *
   * Residual ink is at least `half` from every detection by construction --
   * nearer and the band would have explained it -- so "adjacent" cannot mean
   * touching. One further band-width is what it can mean.
   */
  const half = bandHalfWidthPixels(context, bandPoints);
  const reach = 2 * half;
  // Boxes inflated by `reach`, as a cheap first test before the exact one.
  const reachBoxes = detections.map((d) => bandBoxes(d, reach));

  for (let start = 0; start < ink.length; start += 1) {
    if (ink[start] === 0 || mask[start] === 1 || seen[start] === 1) continue;

    seen[start] = 1;
    stack.push(start);
    let pixels = 0;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    const abutting = new Set<number>();

    while (stack.length > 0) {
      const at = stack.pop()!;
      const x = at % width;
      const y = (at - x) / width;
      pixels += 1;

      /**
       * Which detections this region actually runs alongside, tested against
       * the region's own pixels and the detection's own GEOMETRY.
       *
       * The first version of this compared bounding boxes, and a bounding box
       * says almost nothing about a shape that is mostly not there. A circle's
       * box is a filled square, so every glyph inside a detected circle came
       * back as "beside" it -- the precise overstatement `covers` refuses to
       * make when it treats a circle as an annulus and never a disc. A diagonal
       * line's box is the whole square it spans, so ink at the far corner, a
       * hundred pixels off the segment, read as touching it.
       *
       * A detection already found is never re-tested, so the cost falls away
       * within the first pixels of a region that does run alongside one.
       */
      if (abutting.size < detections.length) {
        for (let d = 0; d < detections.length; d += 1) {
          if (abutting.has(d)) continue;
          const boxes = reachBoxes[d]!;
          let near = false;
          for (const box of boxes) {
            if (x >= box.x0 && x <= box.x1 && y >= box.y0 && y <= box.y1) {
              near = true;
              break;
            }
          }
          if (near && covers(detections[d]!, x, y, reach)) abutting.add(d);
        }
      }
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;

      for (let dy = -1; dy <= 1; dy += 1) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;
          const next = ny * width + nx;
          if (ink[next] === 0 || mask[next] === 1 || seen[next] === 1) continue;
          seen[next] = 1;
          stack.push(next);
        }
      }
    }

    // The extent is inclusive, so a single pixel is 1x1 rather than 0x0 and a
    // long thin stroke clears the floor on its length while a compact speck of
    // the same pixel count does not. Size here means reach, not area.
    const extent = Math.hypot(maxX - minX + 1, maxY - minY + 1);
    if (extent >= floor) {
      located.push({
        bbox: [minX, minY, maxX, maxY] as const,
        pixels,
        abuts: [...abutting].sort((a, b) => a - b),
        seed: start,
      });
    } else {
      diffuseRegions += 1;
      diffusePixels += pixels;
    }
  }

  // Sorted by position so the output is a property of the image rather than of
  // the scan order, exactly as the detector sorts its own findings.
  located.sort(
    (a, b) => a.bbox[1] - b.bbox[1] || a.bbox[0] - b.bbox[0] || b.pixels - a.pixels,
  );

  return {
    totalInk,
    explained,
    mask,
    located,
    diffuse: { regions: diffuseRegions, pixels: diffusePixels },
    perEntity,
    bandPoints,
    minRegionPoints: regionPoints,
  };
}

/**
 * The invariant, checked against the records that are actually emitted.
 *
 * `explained + located + diffuse === totalInk`, where the three terms come from
 * three different representations: `totalInk` from a direct pass over the ink
 * array, `explained` from the coverage mask, and the last two **summed from the
 * region records themselves**. Computing the residual as `totalInk - explained`
 * would make this arithmetic and it would prove nothing — which is precisely the
 * trap ADR 0004 avoids by having `consume` and `record` called from different
 * branches.
 *
 * So one specific future commit fails here: *"cap the issue list at fifty so the
 * interface does not explode."* Capping loses ink, and losing ink is the whole
 * thing this exists to detect.
 */
export function assertReconciled(ledger: RasterLedger): void {
  let located = 0;
  for (const region of ledger.located) located += region.pixels;
  const accounted = ledger.explained + located + ledger.diffuse.pixels;
  if (accounted !== ledger.totalInk) {
    throw new Error(
      `ink ledger does not reconcile: ${String(ledger.explained)} explained + ` +
        `${String(located)} located + ${String(ledger.diffuse.pixels)} diffuse = ` +
        `${String(accounted)}, against ${String(ledger.totalInk)} ink pixels ` +
        `(${String(ledger.totalInk - accounted)} unaccounted)`,
    );
  }
}

/** What the interface prints as the last figure: zero, or the size of the hole. */
export function unaccounted(ledger: RasterLedger): number {
  let located = 0;
  for (const region of ledger.located) located += region.pixels;
  return ledger.totalInk - (ledger.explained + located + ledger.diffuse.pixels);
}

/**
 * The ledger as quality issues, in page points.
 *
 * The conversion is a pure scale and there is **no y flip**, for the reason
 * `toEntities` sets out at length: a raster row index grows downward and so does
 * this contract's page space. Subtracting from the page height would mirror
 * every region about the sheet's centreline and point a reviewer at the wrong
 * half of the drawing.
 *
 * A region abutting a detection carries that detection's id, taken from
 * `region.abuts` — the ledger already measured which detections the region runs
 * alongside, against their geometry and its own pixels. Passing the detection
 * list in again would only invite a caller to hand over a different one than
 * the account was built from. It does **not** say what the region is. The
 * vector path may name a curve because a PDF operator says `c`; a raster
 * detector has ink and nothing else, and a guess about the shape would be this
 * surface overstating exactly where it must not.
 */
export function inkIssues(ledger: RasterLedger, context: DetectorContext): QualityIssue[] {
  const scale = context.pixelsPerPoint;
  const issues: QualityIssue[] = [];

  for (const region of ledger.located) {
    const [x0, y0, x1, y1] = region.bbox;
    // The same `r%04d` scheme `toEntities` mints, so an issue names an entity
    // that exists rather than an index into a list nobody else can see.
    const entityIds = region.abuts.map((index) => `r${String(index).padStart(4, '0')}`);
    const near = entityIds.length > 0 ? ` beside ${entityIds.join(', ')}` : '';

    issues.push({
      kind: 'unrecognised_ink',
      count: 1,
      pageIndex: 0,
      bbox: [x0 / scale, y0 / scale, x1 / scale, y1 / scale] as const,
      detail:
        `${String(region.pixels)} ink pixels no detection accounts for${near}. ` +
        'Located, not identified: this detector attempts lines and circles, and ' +
        'what this is was not determined.',
      ...(entityIds.length > 0 ? { entityIds } : {}),
    });
  }

  if (ledger.diffuse.regions > 0) {
    issues.push({
      kind: 'unrecognised_ink',
      count: ledger.diffuse.regions,
      pageIndex: 0,
      // No bounding box, deliberately. A box per speck is not information, and
      // one box around all of them would claim a region that is not there.
      detail:
        `${String(ledger.diffuse.regions)} unexplained marks totalling ` +
        `${String(ledger.diffuse.pixels)} ink pixels, each smaller than ` +
        `${String(ledger.minRegionPoints)} pt across. On a degraded scan this is ` +
        'most likely sensor speckle, and it is counted rather than classified.',
    });
  }

  return issues;
}
